/**
 * PaginatedList Component - Extended Reusable List
 *
 * Generic component supporting both infinite scroll and classic pagination modes
 * with full keyboard navigation, ARIA accessibility, and optional card container.
 *
 * @module components/ui/PaginatedList
 */

import { memo, type ReactNode, type CSSProperties } from 'react';
import { Stack, Text, Center, Button, Box, Skeleton } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import clsx from 'clsx';
import type { PaginatedListProps, PaginatedListGap } from './types';
import { isInfiniteMode, isPaginationMode } from './types';
import { useListKeyboardNavigation } from './hooks/useListKeyboardNavigation';
import { useDeviceLayout } from '@/hooks/useDeviceLayout';
import { ListHeader } from './components/ListHeader';
import { InfiniteScrollSection } from './components/InfiniteScrollSection';
import { PaginationControls } from './components/PaginationControls';
import styles from './PaginatedList.module.css';

const DEFAULT_SKELETON_COUNT = 3;
const DEFAULT_SKELETON_HEIGHT = 60;

/**
 * Resolves the grouped configuration objects into a flat shape with defaults.
 */
function getMergedConfig<T>(props: PaginatedListProps<T>) {
  const { cardConfig, stateRenderers, behavior, styling } = props;

  return {
    // Card config
    withCard: cardConfig?.enabled ?? false,
    cardHeader: cardConfig?.header,
    showColumnHeaders: cardConfig?.columns?.show ?? false,
    columnHeaders: cardConfig?.columns?.config,
    filterSlot: cardConfig?.filterSlot,
    sortConfig: cardConfig?.sort?.config,
    onSortChange: cardConfig?.sort?.onChange,
    // State renderers
    renderEmpty: stateRenderers?.empty,
    renderError: stateRenderers?.error,
    emptyMessage: stateRenderers?.emptyMessage ?? 'common.noItems',
    // Behavior config
    enableKeyboardNavigation: behavior?.enableKeyboardNavigation ?? true,
    onItemSelect: behavior?.onItemSelect,
    onItemClick: behavior?.onItemClick,
    // Styling config
    gap: styling?.gap ?? 'sm',
    minHeight: styling?.minHeight,
    className: styling?.className,
    skeletonCount: styling?.skeletonCount ?? DEFAULT_SKELETON_COUNT,
  };
}

/**
 * Renders skeleton loading placeholders
 */
function renderSkeletonItems(
  count: number,
  gap: PaginatedListGap,
  t: TFunction
): ReactNode {
  const stackGap = gap === 'none' ? 0 : gap;
  const skeletonItems = Array.from({ length: count }, (_, index) => index);

  return (
    <Stack
      gap={stackGap}
      className={styles.skeletonContainer}
      role="status"
      aria-label={t('common.pagination.loadingItems')}
      aria-busy="true"
    >
      {skeletonItems.map((index) => (
        <div key={`skeleton-${index}`} className={styles.skeletonItem}>
          <Skeleton height={DEFAULT_SKELETON_HEIGHT} animate />
        </div>
      ))}
    </Stack>
  );
}

function PaginatedListInner<T>(props: PaginatedListProps<T>) {
  // Core props (always at top level)
  const {
    data,
    renderItem,
    keyExtractor,
    isLoading,
    hasLoaded = true,
    error,
    'aria-label': ariaLabel,
  } = props;

  // Get merged config (new API takes precedence over legacy)
  const {
    withCard,
    cardHeader,
    showColumnHeaders,
    columnHeaders,
    filterSlot,
    sortConfig,
    onSortChange,
    renderEmpty,
    renderError,
    emptyMessage,
    enableKeyboardNavigation,
    onItemSelect,
    onItemClick,
    gap,
    minHeight,
    className,
    skeletonCount,
  } = getMergedConfig(props);

  const { t } = useTranslation();
  const { isMobile } = useDeviceLayout();

  // Keyboard navigation hook
  const { focusedIndex, handleKeyDown, listRef } = useListKeyboardNavigation({
    itemCount: data.length,
    data,
    onItemSelect,
    enabled: enableKeyboardNavigation && data.length > 0,
  });

  // Determine current state
  const isInitialLoading = (isLoading || !hasLoaded) && data.length === 0;
  const hasError = Boolean(error);
  const isEmpty = data.length === 0 && !isLoading && hasLoaded;

  // Skeleton loading state
  const renderSkeletons = () =>
    renderSkeletonItems(skeletonCount, withCard ? 'none' : gap, t);

  // Error state content
  const renderErrorState = () => {
    // error is guaranteed to be non-null when this function is called (hasError check)
    const errorMessage = error ?? '';

    if (renderError) {
      return renderError(errorMessage);
    }

    return (
      <Center className={clsx(styles.stateContainer, styles.errorState)}>
        <Stack align="center" gap="sm">
          <Text c="red" size="sm">
            {error}
          </Text>
          {(isInfiniteMode(props) || isPaginationMode(props)) && (
            <Button
              variant="light"
              size="xs"
              onClick={
                isInfiniteMode(props)
                  ? props.onLoadMore
                  : () => props.onPageChange(props.currentPage)
              }
            >
              {t('common.pagination.retry')}
            </Button>
          )}
        </Stack>
      </Center>
    );
  };

  // Empty state content
  const renderEmptyState = () => {
    if (renderEmpty) {
      return renderEmpty();
    }

    return (
      <Center className={clsx(styles.stateContainer, styles.emptyState)}>
        <Text c="dimmed" size="sm">
          {t(emptyMessage)}
        </Text>
      </Center>
    );
  };

  // Convert gap to Mantine-compatible value (0 for card mode or when 'none')
  const effectiveGap = withCard || gap === 'none' ? 0 : gap;

  // List content (items + pagination controls)
  const renderListContent = () => {
    const containerStyle =
      minHeight && !withCard
        ? ({
            '--list-min-height':
              typeof minHeight === 'number' ? `${minHeight}px` : minHeight,
          } as CSSProperties)
        : undefined;

    return (
      <Stack
        ref={listRef}
        role="list"
        aria-label={ariaLabel}
        aria-busy={isLoading}
        tabIndex={enableKeyboardNavigation ? 0 : undefined}
        onKeyDown={enableKeyboardNavigation ? handleKeyDown : undefined}
        gap={effectiveGap}
        className={clsx(styles.listContainer, className)}
        style={containerStyle}
      >
        {/* List items */}
        {data.map((item, index) => {
          const isFocused = focusedIndex === index;
          const key = keyExtractor(item, index);
          const isInteractive = !!onItemClick;

          return (
            <Box
              key={key}
              role="listitem"
              tabIndex={isFocused ? 0 : -1}
              aria-selected={isFocused}
              data-focused={isFocused}
              className={clsx(
                styles.item,
                isInteractive && styles.itemInteractive,
                isFocused && styles.itemFocused
              )}
              onClick={
                isInteractive ? () => onItemClick?.(item, index) : undefined
              }
            >
              {renderItem(item, index, isFocused)}
            </Box>
          );
        })}

        {/* Infinite scroll controls */}
        {isInfiniteMode(props) && (
          <InfiniteScrollSection
            hasNextPage={props.hasNextPage}
            isLoading={isLoading}
            showButton={props.showLoadMoreButton}
            onLoadMore={props.onLoadMore}
            threshold={props.loadMoreThreshold}
          />
        )}

        {/* Pagination controls */}
        {isPaginationMode(props) && (
          <PaginationControls
            currentPage={props.currentPage}
            totalPages={props.totalPages}
            totalItems={props.totalItems}
            pageSize={props.pageSize}
            onPageChange={props.onPageChange}
            siblings={props.siblings}
            boundaries={props.boundaries}
            isLoading={isLoading}
          />
        )}

        {/* Live region for screen readers */}
        <div role="status" aria-live="polite" className={styles.srOnly}>
          {isLoading && t('common.pagination.loading')}
        </div>
      </Stack>
    );
  };

  // Determine what content to show inside the card or as standalone
  const getContent = () => {
    if (isInitialLoading) return renderSkeletons();
    if (hasError) return renderErrorState();
    if (isEmpty) return renderEmptyState();
    return renderListContent();
  };

  // Card mode wrapper
  if (withCard) {
    return (
      <div className={styles.cardWrapper}>
        {/* Mobile: Filters above card */}
        {isMobile && filterSlot && (
          <div className={styles.mobileFilterSlot}>{filterSlot}</div>
        )}

        <div className={styles.card}>
          {/* Card header (title and actions only) */}
          {cardHeader && (
            <div className={styles.cardHeader}>
              <div className={styles.cardHeaderContent}>{cardHeader}</div>
            </div>
          )}

          {/* Filter row (desktop only) */}
          {!isMobile && filterSlot && (
            <div className={styles.filterRow}>{filterSlot}</div>
          )}

          {/* Column headers (desktop only, when data is present) */}
          {showColumnHeaders &&
            columnHeaders &&
            !isInitialLoading &&
            !hasError &&
            !isEmpty && (
              <ListHeader
                columns={columnHeaders}
                sortConfig={sortConfig}
                onSortChange={onSortChange}
              />
            )}

          {/* Main content area */}
          <div className={styles.cardContent}>{getContent()}</div>
        </div>
      </div>
    );
  }

  // Default: no card wrapper (backward compatible)
  return getContent();
}

// Memoize the component while preserving generic type
export const PaginatedList = memo(
  PaginatedListInner
) as typeof PaginatedListInner;
